/**
 * Admin settings: which bridges count as internet-facing, what the app calls
 * itself, the API tokens other programs hold, and the local account's password.
 */

import { useEffect, useState } from 'react';
import { DEFAULT_EDGE_BRIDGES, MIN_PASSWORD_LENGTH } from '@proxmon/shared';
import { useStore } from '../state/store.ts';
import { ApiTokens } from './ApiTokens.tsx';

export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);
  const changePassword = useStore((s) => s.changePassword);
  const notify = useStore((s) => s.notify);

  const [edgeDraft, setEdgeDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setEdgeDraft(settings.edgeBridges.join(', '));
  }, [settings]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const apply = async (patch: Parameters<typeof updateSettings>[0]) => {
    setBusy(true);
    try {
      await updateSettings(patch);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not save settings.');
    } finally {
      setBusy(false);
    }
  };

  const saveEdgeBridges = async () => {
    const bridges = edgeDraft
      .split(',')
      .map((b) => b.trim().toLowerCase())
      .filter(Boolean);
    await apply({ edgeBridges: bridges.length ? bridges : DEFAULT_EDGE_BRIDGES });
    notify('info', 'Internet-facing bridges updated.');
  };

  const submitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPwError(null);
    if (next.length < MIN_PASSWORD_LENGTH) {
      setPwError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setPwOpen(false);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Settings">
        <header>
          <h2>Settings</h2>
          <button type="button" className="close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="body">
          {!settings ? (
            <p className="empty">Loading…</p>
          ) : (
            <>
              <div className="setting-row" style={{ display: 'block' }}>
                <div className="text" style={{ marginBottom: 8 }}>
                  <strong>Internet-facing bridges</strong>
                  <span>
                    Guests on these bridges are highlighted as exposed. Comma-separated, normally just
                    vmbr0.
                  </span>
                </div>
                <div className="ip-editor">
                  <input
                    value={edgeDraft}
                    onChange={(event) => setEdgeDraft(event.target.value)}
                    placeholder="vmbr0"
                    spellCheck={false}
                    aria-label="Internet-facing bridges"
                  />
                  <button type="button" className="btn" disabled={busy} onClick={() => void saveEdgeBridges()}>
                    Save
                  </button>
                </div>
              </div>

              <ApiTokens />

              <div className="setting-row" style={{ display: 'block' }}>
                <div className="text" style={{ marginBottom: 8 }}>
                  <strong>Password</strong>
                  <span>Changing it signs out every other device.</span>
                </div>
                {pwOpen ? (
                  <form onSubmit={submitPassword}>
                    {pwError && <div className="form-error">{pwError}</div>}
                    <div className="field">
                      <label htmlFor="current-password">Current password</label>
                      <input
                        id="current-password"
                        type="password"
                        value={current}
                        autoComplete="current-password"
                        onChange={(event) => setCurrent(event.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="new-password">New password</label>
                      <input
                        id="new-password"
                        type="password"
                        value={next}
                        autoComplete="new-password"
                        onChange={(event) => setNext(event.target.value)}
                      />
                      <div className="hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="submit" className="btn primary" disabled={busy}>
                        Change password
                      </button>
                      <button type="button" className="btn ghost" onClick={() => setPwOpen(false)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button type="button" className="btn" onClick={() => setPwOpen(true)}>
                    Change password…
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="foot">
          <button type="button" className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
